/**
 * UserWaitingOnBot
 *
 * Purpose:
 * Renders the UI shown while the user is waiting on a bot response,
 * driven entirely by `ConversationStatusProvider` state.
 *
 * Behavior:
 * - Shows `Ellipsis` when `isUserWaiting` is true.
 * - Shows `BotTimedOutMessage` when `didTimeout` is true; that component
 *   scrolls itself into view on mount and emits timeout analytics.
 * - Returns `null` when the conversation is `inactive`.
 *
 *
 * Notes:
 * The "BotTakingAwhile" message is handled within a separate component because it's recorded & rendered
 *  as an activity in the transcript, whereas these components are not.
 * Eventually, we may want to refactor the timeout message to be driven by activities as well.
 */
import React from 'react';
import { Ellipsis } from '..';
import { InputMode } from '../../hooks';
import { AmpFlagSet } from '../../amplitude';

import { MessageRenderer } from '../../interfaces/messageRenderer';
import { useConversationStatus } from '../../contextProviders/ConversationStatusContext';
import { BotTimedOutMessage } from './BotTimedOut';

// Timing constants are now owned by ConversationStatusContext

// Simple render: rely on provider props and return elements directly

export type BotMessageDeps = {
    featureFlags: AmpFlagSet;
    sendMessage: (message: string) => void | undefined;
    sendPostBack: (message: string) => void | undefined;
    sendEvent: (name: string, value: any) => void | undefined;
    setInputMode: (inputMode: InputMode) => void;
    messageRenderer: MessageRenderer;
};

export type UserWaitingMessageProps = {
    botMessageDeps: BotMessageDeps;
};

export const UserWaitingMessage: React.FC<UserWaitingMessageProps> = ({
    botMessageDeps,
}: UserWaitingMessageProps) => {
    const { isUserWaiting, didTimeout, inactive } = useConversationStatus();

    if (inactive) return null;
    if (didTimeout) {
        return <BotTimedOutMessage botMessageDeps={botMessageDeps} />;
    }
    if (isUserWaiting) return <Ellipsis />;
    return null;
};

export default UserWaitingMessage;
